/**
 * Состояние машины — единственное место, где оно выводится.
 *
 * В Фазе 0 это правило оказалось написано дважды и по-разному: в утилите
 * блокировки и в сквозном тесте, с расходящейся трактовкой пустого значения.
 * Правило семантическое, поэтому живёт в одном месте, а список и карта им
 * пользуются, а не повторяют его.
 */

import type { Device, Position } from './api'
import { knotsToKmh } from './api'
import { num } from './format'
import type { Lang } from './i18n'

/**
 * `quiet` — серое «нет свежих данных», а не «нет связи» (решение владельца
 * 28.09). FMC150 на стоянке с выключенным зажиганием по замыслу не шлёт
 * записей (экономия трафика SIM), и Traccar через ~10 минут зовёт его
 * offline, хотя трекер исправен и команды до него доходят. Поэтому молчащий
 * трекер на стоянке — это «стоит», а «нет свежих данных» — только когда он
 * замолчал с включённым зажиганием или в движении, либо молчит дольше 72 ч
 * (решение владельца 28.09: выходные и праздники — обычная стоянка).
 */
export type Status = 'move' | 'park' | 'block' | 'quiet'

/** Молчание дольше этого — «нет свежих данных» и у стоящей машины (72 ч). */
export const LONG_SILENCE_MS = 72 * 60 * 60 * 1000

/** Ниже этого порога считаем, что машина стоит: GPS шумит и на месте. */
const MOVING_KMH = 3

/**
 * Поднят ли выход блокировки.
 *
 * Traccar называет DOUT1 у Teltonika как out1. Если имя не разобрано, тот же
 * выход приходит безымянным номером io179. Пустое значение и отсутствие ключа
 * означают «не поднят»: считать неизвестность блокировкой опаснее — диспетчер
 * решит, что машина заперта, и не станет её запирать на самом деле.
 */
export function isBlocked(position: Position | undefined): boolean {
  if (!position) return false
  const raw = position.attributes?.out1 ?? position.attributes?.io179
  if (raw === undefined || raw === null || raw === '') return false
  if (typeof raw === 'boolean') return raw
  if (typeof raw === 'number') return raw !== 0
  if (typeof raw === 'string') return raw !== '0' && raw.toLowerCase() !== 'false'
  return false
}

export function statusOf(
  device: Device,
  position: Position | undefined,
  now: number = Date.now(),
): Status {
  if (isBlocked(position)) return 'block'
  const moving = position !== undefined && knotsToKmh(position.speed) >= MOVING_KMH
  if (device.status === 'online') return moving ? 'move' : 'park'
  // Трекер молчит. Стоянка с выключенным зажиганием — так задумано.
  if (!position) return 'quiet'
  const last = Date.parse(device.lastUpdate ?? '')
  if (Number.isNaN(last) || now - last > LONG_SILENCE_MS) return 'quiet'
  if (moving || ignitionOn(position) === true) return 'quiet'
  return 'park'
}

export function speedKmh(position: Position | undefined): number {
  return position ? knotsToKmh(position.speed) : 0
}

/** Бортовое напряжение приходит атрибутом power в вольтах. */
export function voltage(position: Position | undefined, lang: Lang): string {
  const raw = position?.attributes?.power
  return typeof raw === 'number' ? num(raw, lang, { min: 1, max: 1 }) : '—'
}

/**
 * Общий пробег машины. Traccar считает его в метрах.
 *
 * Чисел два, и человеку нужно то, что на панели. Пробег по щитку приходит с
 * шины (AVL 87) и лежит в `obdOdometer` — именно его водитель видит глазами и
 * вписывает в документы при передаче машины. Собственный счётчик трекера
 * (`totalDistance`) начинается с нуля в момент установки: показать его вместо
 * щитка значит разойтись и с бумагами, и с самой машиной.
 *
 * Счётчик трекера остаётся запасным: у машины без шины CAN другого пробега нет
 * вовсе, и ноль на экране был бы хуже, чем приблизительное число.
 */
export function odometerKm(position: Position | undefined, lang: Lang): string {
  const dashboard = position?.attributes?.obdOdometer
  const own = position?.attributes?.totalDistance
  const meters = typeof dashboard === 'number' ? dashboard : own
  if (typeof meters !== 'number') return '—'
  return num(Math.round(meters / 1000), lang)
}

export function ignitionOn(position: Position | undefined): boolean | undefined {
  const raw = position?.attributes?.ignition
  return typeof raw === 'boolean' ? raw : undefined
}

/** Чем машина поедет: топливо в баке или заряд тяговой батареи. */
export type Energy = { kind: 'fuel' | 'charge'; percent: number }

/** Что происходит с зарядкой электромобиля. */
export type Charging = 'charging' | 'plugged' | 'unplugged'

/**
 * Проценты и ничего кроме процентов.
 *
 * У Teltonika ширина AVL 89 — байт, до 255, и часть машин может слать не
 * проценты, а сырую шкалу. Тогда «128» означает половину бака. Показать это
 * числом нельзя, а обрезать до ста — соврать «полный бак» при половине.
 * Поэтому значение вне 0..100 считается неизвестным: прочерк честнее.
 */
function percent(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null
  return raw >= 0 && raw <= 100 ? raw : null
}

/**
 * Уровень топлива или заряда.
 *
 * Топливо Traccar разбирает сам и кладёт в `fuelLevel` (AVL 89). Заряд тяговой
 * батареи (AVL 152) он не знает — тот приходит сырым числом `io152`. Проверено
 * на живом Traccar 6.15.3, подробности в `docs/can-audit.md`.
 *
 * У гибрида приходит и то и другое. Показываем топливо: на нём машина доедет
 * и без розетки, а диспетчеру важно именно это.
 */
export function energyLevel(position: Position | undefined): Energy | null {
  const fuel = percent(position?.attributes?.fuelLevel)
  if (fuel !== null) return { kind: 'fuel', percent: fuel }
  const charge = percent(position?.attributes?.io152)
  if (charge !== null) return { kind: 'charge', percent: charge }
  return null
}

/**
 * Кабель зарядки (AVL 914) и сам заряд (AVL 915).
 *
 * Оба приходят сырыми числами — Traccar их не разбирает. Отсутствие обоих
 * означает «неизвестно», а не «не заряжается»: у машин с ДВС их нет вовсе, да
 * и у eSprinter в списке поддержанных параметров их не оказалось.
 */
export function chargingState(position: Position | undefined): Charging | undefined {
  const plugged = position?.attributes?.io914
  const charging = position?.attributes?.io915
  if (typeof plugged !== 'number' && typeof charging !== 'number') return undefined
  if (charging === 1) return 'charging'
  return plugged === 1 ? 'plugged' : 'unplugged'
}

/**
 * Количество с шины: литры, обороты. Ноль — честное значение (пустой бак,
 * заглушённый двигатель), а отрицательное или нечисловое — неизвестность.
 * Показывается целым: десятые доли литра на экране ничего не решают.
 */
function amount(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return null
  return Math.round(raw)
}

/** Топливо в баке, литры (AVL 84). Traccar уже применил множитель 0,1. */
export function fuelLiters(position: Position | undefined): number | null {
  return amount(position?.attributes?.fuel)
}

/** Объём бака из паспорта машины, литры. Его вписывают в управлении парком. */
export function tankLiters(device: Device | undefined): number | null {
  const raw = device?.attributes?.tankLiters
  return typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : null
}

/**
 * Прислан ли объём бака учётной системой (KEPwise).
 *
 * Такое число живёт у неё: правка здесь разошлась бы с карточкой машины, и
 * ночная сверка молча вернула бы прежнее. Поэтому поле запирается, а не
 * притворяется редактируемым.
 */
export function tankFromLink(device: Device | undefined): boolean {
  return tankLiters(device) !== null && device?.attributes?.tankLitersFrom === 'link'
}

/**
 * Объём бака из поля ввода. Пустое или нечисловое поле — «не трогать»: нулём
 * оно не становится, а границы проверяет сервер.
 */
export function typedTank(text: string): number | undefined {
  const cleaned = text.trim().replace(',', '.')
  const value = Number(cleaned)
  return cleaned && Number.isFinite(value) ? value : undefined
}

/** Остаток в баке. `estimated` — посчитан из процентов, а не пришёл с шины. */
export type FuelVolume = { liters: number; estimated: boolean }

/**
 * Сколько литров в баке.
 *
 * Литры с шины главнее: у MAN TGE и Crafter они приходят сами. Sprinter,
 * Transit и Citan отдают только проценты — для них остаток считается от
 * объёма бака из паспорта. Пересчёт линейный, а бак не прямоугольный, поэтому
 * такое число помечено как примерное: сверять его с чеком до литра нельзя.
 */
export function fuelVolume(
  position: Position | undefined,
  tank: number | null,
): FuelVolume | null {
  const measured = fuelLiters(position)
  if (measured !== null) return { liters: measured, estimated: false }
  const level = percent(position?.attributes?.fuelLevel)
  if (level === null || tank === null) return null
  return { liters: Math.round((level / 100) * tank), estimated: true }
}

/**
 * Не хватает ли объёма бака, чтобы показать литры.
 *
 * Подсказка «укажите объём бака» нужна только там, где она что-то изменит:
 * проценты с шины есть, литров нет, бак не заполнен. У машины с литрами с
 * шины, у электромобиля и у машины без шины вовсе просить нечего.
 */
export function tankMissing(position: Position | undefined, tank: number | null): boolean {
  if (tank !== null || fuelLiters(position) !== null) return false
  return percent(position?.attributes?.fuelLevel) !== null
}

/**
 * Обороты двигателя (AVL 85). При выключенном зажигании — «нет»: шина молчит,
 * а трекер кладёт в запись последние услышанные обороты (29.09: стоящая
 * машина «на 495 об/мин»).
 */
export function rpm(position: Position | undefined): number | null {
  if (ignitionOn(position) === false) return null
  return amount(position?.attributes?.rpm)
}

/**
 * Сколько сожжено с момента установки трекера, литры (AVL 107).
 *
 * Счётчик накопительный и за период ничего не говорит: расход за день или за
 * неделю — это разность, и её считает Traccar в отчётах, а не приложение.
 */
export function fuelUsedLiters(position: Position | undefined): number | null {
  return amount(position?.attributes?.fuelUsed)
}

/**
 * VIN (AVL 325).
 *
 * Трекер шлёт его только при изменении, поэтому сам по себе он живёт в одной
 * позиции из тысяч. В каждую следующую его переносит Traccar
 * (`processing.copyAttributes` в compose.yaml) — без этого VIN почти всегда
 * был бы пуст. Номер не из семнадцати знаков считается недочитанным: его
 * перепишут в документы, и неполный хуже отсутствующего.
 */
export function vin(position: Position | undefined): string | null {
  const raw = position?.attributes?.vin
  if (typeof raw !== 'string') return null
  const value = raw.trim()
  return value.length === 17 ? value : null
}
